import { useCallback } from 'react';

import { usePageAnchors } from '@/contexts/pageAnchorsContext';
import slugify from '@/formBuilder/schema/slugify';

/**
 * Publishes one on-page anchor to the section rail and hands back a ref to attach to the element
 * it should scroll to:
 *
 *     const anchorRef = usePageAnchor('Medications');
 *     return <Card ref={anchorRef}>…</Card>;
 *
 * The rail lists anchors in document order, so a page of draggable cards needs to do nothing when
 * the cards are reordered. Pass `id` when the label is not unique on the page, or when the label
 * changes but the anchor should keep its identity.
 */
const usePageAnchor = (label: string, id?: string) => {
  const { registerAnchor } = usePageAnchors();
  const anchorId = id ?? slugify(label, 'anchor');

  return useCallback(
    (element: HTMLElement | null) => registerAnchor({ id: anchorId, label }, element),
    [anchorId, label, registerAnchor]
  );
};

export default usePageAnchor;
